/**
 * Geocode a free-text address via Google's Geocoding REST API.
 *
 * Used by bulk-load workflows (CSV import via MyDropzone, per-row
 * re-geocode in BulkLoadLocationsCN) where interactive Places
 * autocomplete isn't viable. The user-facing add/edit forms use the
 * `react-places-autocomplete` component (K1 / MYD-189) — this helper
 * exists for the non-interactive bulk path.
 *
 * Per K3 (MYD-190), this function does NOT call the backend
 * `/api/geocode/search` Photon proxy — that endpoint is now reserved
 * for server-side fallback (e.g., `evaluationSiteController.createSite`
 * when an address-only payload arrives via the API).
 *
 * The Google API key is read from `process.env.REACT_APP_GOOGLE_API_KEY`
 * by default; pass `apiKey` explicitly to override (legacy callers).
 *
 * @param {string} address — free-text address query
 * @param {string} [apiKey] — overrides REACT_APP_GOOGLE_API_KEY
 * @returns {Promise<{ lat: number, lng: number }>}
 * @throws  if the address is empty, the API key is missing, the
 *          response status is not OK, or no results are returned
 */
const geocodeAddress = async (address, apiKey) => {
	if (!address || !address.trim()) {
		throw new Error('Geocoding failed: empty address')
	}

	const key = apiKey || process.env.REACT_APP_GOOGLE_API_KEY
	if (!key) {
		throw new Error('Geocoding failed: REACT_APP_GOOGLE_API_KEY not configured')
	}

	const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
		address,
	)}&key=${encodeURIComponent(key)}`

	try {
		const response = await fetch(url)
		if (!response.ok) {
			throw new Error(`Geocoding failed: HTTP ${response.status}`)
		}
		const data = await response.json()
		// Google statuses: OK, ZERO_RESULTS, OVER_QUERY_LIMIT, REQUEST_DENIED,
		// INVALID_REQUEST, UNKNOWN_ERROR. Treat anything not-OK as failure so
		// the caller can surface the problem (rate limit, denied key, etc.).
		if (data.status !== 'OK' || !Array.isArray(data.results) || data.results.length === 0) {
			throw new Error(`Geocoding failed: ${data.status || 'no results'}`)
		}
		const loc = data.results[0]?.geometry?.location
		if (!loc || !Number.isFinite(loc.lat) || !Number.isFinite(loc.lng)) {
			throw new Error('Geocoding failed: missing coordinates')
		}
		return { lat: loc.lat, lng: loc.lng }
	} catch (error) {
		console.error('Error geocoding address:', error)
		throw error
	}
}

export default geocodeAddress
